import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Transition } from "react-transition-group";
import "./InvestmentPhotoLightbox.scss";
import { SVG } from "shared";
import { SVG_ENUM } from "enums";

interface LightboxFile {
  fileUuid: string;
  file: {
    uuid: string;
    name: string;
    url: string;
  };
}

interface InvestmentPhotoLightboxProps {
  isOpen: boolean;
  onClose: () => void;
  files: LightboxFile[];
  title?: string;
}

const InvestmentPhotoLightbox = ({
  isOpen,
  onClose,
  files,
  title,
}: InvestmentPhotoLightboxProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % files.length);
  }, [files.length]);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + files.length) % files.length);
  }, [files.length]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, goNext, goPrev, onClose]);

  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(0);
      document.querySelector("html")?.classList.add("modal-open");
    } else {
      document.querySelector("html")?.classList.remove("modal-open");
    }
  }, [isOpen]);

  const duration = 250;

  return createPortal(
    <Transition in={isOpen} timeout={duration} unmountOnExit>
      {(state) => (
        <div className={`lightbox lightbox--${state}`} onClick={onClose}>
          <div
            className="lightbox__dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="lightbox__header">
              <span className="lightbox__title">
                {title ? `Podgląd zdjęcia: ${title}` : "Zdjęcia"}
              </span>
              <button className="lightbox__close" onClick={onClose}>
                <SVG type={SVG_ENUM.CLOSE} />
              </button>
            </div>

            <div className="lightbox__image-wrapper">
              {files[currentIndex] && (
                <img
                  key={files[currentIndex].fileUuid}
                  src={files[currentIndex].file?.url}
                  alt={files[currentIndex].file?.name ?? "Zdjęcie"}
                  className="lightbox__image"
                />
              )}

              {files.length > 1 && (
                <>
                  <button
                    className="lightbox__arrow lightbox__arrow--prev"
                    onClick={goPrev}
                  >
                    <SVG type={SVG_ENUM.BACK_CHEVRON} />
                  </button>
                  <button
                    className="lightbox__arrow lightbox__arrow--next"
                    onClick={goNext}
                  >
                    <SVG type={SVG_ENUM.BACK_CHEVRON} />
                  </button>
                </>
              )}
            </div>

            {files.length > 1 && (
              <div className="lightbox__dots">
                {files.map((_, i) => (
                  <button
                    key={i}
                    className={`lightbox__dot ${
                      i === currentIndex ? "lightbox__dot--active" : ""
                    }`}
                    onClick={() => setCurrentIndex(i)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </Transition>,
    document.querySelector(".App") ?? document.body,
  );
};

export default InvestmentPhotoLightbox;
